import { useEffect, useState } from 'react'

import { ActionIconEnum } from 'common/enums/ActionIcon'
import { useNavBar } from 'hooks/useNavBar'
import { Button, Flex, formatToBRL, Text } from 'components/InterUI/InterUI'
import { FlexRow, ListDescription, Separator } from 'components'
import { Icons } from 'utils/icons'
import RepurchaseGift from 'assets/png/repurchase_gift.png'
import { TypesRoutes } from 'routes/mixedRoutes/types'
import { useHistory } from 'react-router-dom'
import useBoundState from 'store'
import { useFilterTicketsToRedeem } from 'hooks/useFilterTicketsToRedeem'
import BaseBridge from 'config/bridge/BaseBridge'

import { Counter } from '@interco/inter-ui/components/Counter'

import * as S from './Repurchase.styles'
import { HelpBottomSheet } from './HelpBottomSheet'
import {
  analyticsGeneralConditionsTag,
  analyticsInfoLegaisTag,
  analyticsRepurchaseLoad,
  analyticsSubmitRepurchaseTag,
  analyticsTooltipTag,
} from './Repurchase.tags'

export const Repurchase = () => {
  const history = useHistory()

  const contentCapitalization = useBoundState((state) => state.contentCapitalization)
  const contentTitle = useBoundState((state) => state.contentTitle)
  const tickets = useBoundState((state) => state.tickets)
  const ticketsToRedeem = useFilterTicketsToRedeem({ tickets: tickets.completed })
  const repurchase = useBoundState((state) => state.repurchase)

  const monitoring = useBoundState((state) => state.monitoring)
  const SINGLE_TICKET_PRICE = 3
  const redemptionAmount = tickets.totalRedemptionAmount ?? 0
  const defaultQuantity = ticketsToRedeem.tickets.length < 20 ? ticketsToRedeem.tickets.length : 20

  const [isHelpBottomSheetOpen, setIsHelpBottomSheetOpen] = useState(false)
  const [quantity, setQuantity] = useState(defaultQuantity)
  const [payout, setPayout] = useState(SINGLE_TICKET_PRICE * defaultQuantity)
  const [payAmount, setPayAmount] = useState(payout - redemptionAmount)
  const diffDescription = payAmount >= 0 ? 'Diferença a pagar' : 'Diferença a receber'

  useNavBar({
    navbarTitle: `Resgatar novo ${contentTitle.value1Lowercase}`,
    actionIcons: {
      rightIcon: ActionIconEnum.FAQ,
      leftIcon: ActionIconEnum.BACK,
    },
    backListener: () => history.push(TypesRoutes.AFTER_SALES),
  })

  useEffect(() => {
    analyticsRepurchaseLoad()
    monitoring({
      eventName: 'load_repurchase_page',
      params: { redemption_amount: `${formatToBRL(redemptionAmount)}` },
    })
  }, [])

  const helpIcon = <Icons.Help width={16} height={16} color="var(--primary500)" />

  const toggleHelpBottomSheet = () => {
    setIsHelpBottomSheetOpen(!isHelpBottomSheetOpen)
  }

  const Banner = (
    <S.Banner data-testid="banner">
      <img src={RepurchaseGift} alt="Repurchase Gift" />
      <S.BannerText>
        <Text variant="headline-h3" as="h3" semiBold color="primary" colorWeight={500}>
          Use o valor disponível e mantenha suas chances por mais 12 meses
        </Text>
        <Text variant="caption-1" colorWeight={400}>
          Agora são 16 chances de <br />
          ganhar R$ 7.000,00
        </Text>
      </S.BannerText>
    </S.Banner>
  )

  const Details = (
    <S.Details data-testid="details">
      <FlexRow
        title={`Valor por ${contentTitle.value1Lowercase}`}
        titleVariant="caption-1"
        value={formatToBRL(SINGLE_TICKET_PRICE)}
      />
      <FlexRow title="Valor total" titleVariant="caption-1" value={formatToBRL(payout)} />
      <FlexRow
        title="Valor de resgate"
        titleVariant="caption-1"
        value={
          <Text variant="caption-1" className="greenText" bold colorWeight={500}>
            - {formatToBRL(redemptionAmount)}
          </Text>
        }
      />
      <FlexRow
        title={
          <S.HelpIcon
            onClick={() => {
              analyticsTooltipTag({ value: formatToBRL(payout) })
              toggleHelpBottomSheet()
            }}
          >
            {diffDescription} {helpIcon}
          </S.HelpIcon>
        }
        titleVariant="caption-1"
        value={formatToBRL(Math.abs(payAmount))}
      />
    </S.Details>
  )

  const handleUpdateCounter = (amount: number) => {
    setQuantity(amount)
    const newPayout = amount * SINGLE_TICKET_PRICE
    setPayout(newPayout)
    setPayAmount(newPayout - redemptionAmount)
  }

  const CounterComponent = (
    <S.Counter data-testid="counter">
      <Flex direction="row">
        <Text variant="headline-h3" as="h3" semiBold>
          Quantidade
        </Text>
        <Counter
          data-testid="counter-input"
          min={1}
          max={20}
          value={quantity}
          onUpdate={(amount) => handleUpdateCounter(amount)}
        />
      </Flex>
      <Text variant="body-3">Máximo de 20 unidades</Text>
    </S.Counter>
  )

  const FooterPayment = (
    <>
      <Text variant="headline-h3" as="h3" semiBold colorWeight={500}>
        Forma de pagamento
      </Text>
      <ListDescription
        data-testid="payment-methods"
        iconLeft={<Icons.Wallet width={24} height={24} color="var(--primary500)" />}
        withBorder
        borderColor="var(--gray200)"
      >
        <Text variant="body-3" bold colorWeight={600}>
          Débito Inter
        </Text>
      </ListDescription>
      <S.Payment>
        <Flex direction="column" justifyContent="center">
          <Text variant="caption-1" colorWeight={500}>
            {diffDescription}
          </Text>

          <Text variant="headline-h3" as="h3" semiBold color="primary">
            {formatToBRL(Math.abs(payAmount))}
          </Text>
        </Flex>
        <Button
          data-testid="pay-button"
          onClick={() => {
            analyticsSubmitRepurchaseTag()

            repurchase({
              repurchaseQuantity: quantity,
              history,
              pathname: TypesRoutes.REDEMPTION_RECEIPT,
            })
          }}
          fullWidth
          disabled={false}
        >
          Concluir
        </Button>
      </S.Payment>
    </>
  )

  const renderFooterDescription = () => {
    const openGeneralConditions = () => {
      analyticsGeneralConditionsTag()
      BaseBridge.openPdf(
        'https://static.bancointer.com.br/seguros/capitalization/CondicoesGeraisSortezinha.pdf',
        'Condições Gerais',
      )
    }
    const openRegistrationForm = () => {
      history.push(TypesRoutes.REGISTRATION_FORM)
    }

    const openLegalInfo = () => {
      analyticsInfoLegaisTag()
      BaseBridge.openPdf(
        'https://static.bancointer.com.br/seguros/capitalization/InformacoesLegaisSortezinha.pdf',
        'Informações Legais',
      )
    }

    return (
      <Text variant="caption-1" color="primary" colorWeight={600}>
        Ao concluir declaro estar de acordo com{' '}
        <S.Link onClick={openGeneralConditions}>Condições Gerais</S.Link>,{' '}
        <S.Link onClick={openLegalInfo}>Informações legais</S.Link> e{' '}
        <S.Link onClick={openRegistrationForm}>Ficha de Cadastro</S.Link> da{' '}
        {contentCapitalization.value1}.
      </Text>
    )
  }

  return (
    <S.Container>
      <S.Content>
        {Banner}
        <Text variant="headline-h3" as="h3" semiBold>
          Resumo
        </Text>
        {Details}
        {CounterComponent}
      </S.Content>
      <Separator color="var(--gray100)" margin="8px 0" borderWidth="8px" />
      <S.Content>
        {FooterPayment}
        {renderFooterDescription()}
      </S.Content>
      {isHelpBottomSheetOpen && (
        <HelpBottomSheet isPaying={payAmount >= 0} handleCloseBottomSheet={toggleHelpBottomSheet} />
      )}
    </S.Container>
  )
}
